/**
 * Logo — the Attic mark, and optionally the drawn wordmark beside it.
 *
 * 🔥 The wordmark is ARTWORK, not type. This component used to render the word
 * "Attic" in the display face next to a raster mark, which meant the product
 * showed a different wordmark from every other surface the moment the display
 * face changed — and it changed twice (Fraunces, then Newsreader, now Petrona).
 * The letterforms in the real wordmark are drawn; no font reproduces them.
 *
 * Light and dark use two different constructions per the brand guide: an
 * espresso outline on cream, and no outline on dark. That swap lives in
 * Logo.module.css so both files ship and CSS picks one.
 */
import markOutline from '../../assets/attic-mark-outline.svg';
import mark from '../../assets/attic-mark.svg';
import wordmarkOutline from '../../assets/attic-wordmark-outline.svg';
import wordmark from '../../assets/attic-wordmark.svg';
import styles from './Logo.module.css';

export interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  showWordmark?: boolean;
  align?: 'center' | 'start';
}

/** Heights in px. The guide's floors are 40px for the standalone A and 120px
 *  wide for the full wordmark, so `sm` sits just above both. */
const MARK_SIZE = { sm: 32, md: 48, lg: 64 } as const;

/* Intrinsic ratios, straight off each viewBox, so width is never guessed. */
const MARK_RATIO = 461.88 / 500.25;
const WORDMARK_RATIO = 1328.45 / 500.11;

export function Logo({ size = 'md', showWordmark = true, align = 'center' }: LogoProps) {
  const h = MARK_SIZE[size];
  // The wordmark already contains the A, so showing both would repeat it.
  const src = showWordmark
    ? { light: wordmarkOutline, dark: wordmark, ratio: WORDMARK_RATIO }
    : { light: markOutline, dark: mark, ratio: MARK_RATIO };
  const w = Math.round(h * src.ratio);

  return (
    <div
      data-testid="attic-logo"
      className={styles.logo}
      style={{ justifyContent: align === 'center' ? 'center' : 'flex-start' }}
    >
      <img
        src={src.light}
        alt="Attic"
        width={w}
        height={h}
        className={`${styles.mark} ${styles.onLight}`}
        style={{ height: h }}
      />
      <img
        src={src.dark}
        alt=""
        aria-hidden="true"
        width={w}
        height={h}
        className={`${styles.mark} ${styles.onDark}`}
        style={{ height: h }}
      />
    </div>
  );
}
